import { useMemo } from 'react';

import { BlockText, Button, DarkTooltip, Flexbox, SimpleText, Text } from '@morressier/ts';

import { Grid } from '@material-ui/core';
import { FaRegClock, FaVideo, FaChevronLeft, FaBan, FaTag } from 'react-icons/fa';

import MorressierMarkdown from 'components/MorressierMarkdown';
import { FeatureFlags } from 'config/featureFlags';
import { Banner } from 'containers/EventHome/styles';
import { SimpleCard } from 'containers/SessionsList/styles';
import { useTimeToNow } from 'hooks/useTimeNow';
import { UserClassReg } from 'icons';
import { useAuthContext } from 'store';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { logEvent } from 'utils/eventTracking';
import { getTimeText } from 'utils/timeAndDateHelper';

import * as ContainerStyles from '../styles';
import { SessionChairsWithIcon, SessionParticipantsWithImage } from './components';
import { getSpotsAvailability } from './helpers';
import { useSessionTime } from './hooks';
import RtmpHeaderOptions from './Rtmp/RtmpHeaderOptions';
import * as Styles from './styles';

type LiveStatus = MorressierSession['main_stage']['live_status'];
type getHeaderMessageProps = {
  type: MorressierSessionType;
  hasRecordings?: boolean;
  isHappeningNow: boolean;
  isPostSession: boolean;
  timeToNow: string | null;
  isRtmpSession: boolean;
  isOrganizer?: boolean;
  isChair?: boolean;
  isSpeaker?: boolean;
  isLessThan1hrToStartTime: boolean;
  liveStatus?: LiveStatus;
  showLoginRequired: boolean;
  sessionName: string;
  event: Partial<MorressierEvent>;
};

function getHeaderMessages({
  type,
  hasRecordings,
  isHappeningNow,
  isPostSession,
  timeToNow,
  isRtmpSession,
  isOrganizer,
  isChair,
  isSpeaker,
  isLessThan1hrToStartTime,
  liveStatus,
  showLoginRequired,
  sessionName,
  event,
}: getHeaderMessageProps) {
  const sessionTypeOptions = {
    PRESENTATIONS: {
      //
      title: hasRecordings
        ? ''
        : `Session has ended. ${
            event?.type !== 'Onsite' ? 'Recordings will be available soon' : ''
          }`,
      showSubtitle: true,
      loginRequiredMessage: 'Only Attendees can watch Sessions',
    },
    NETWORKING: {
      title: 'The Session has ended.',
      showSubtitle: showLoginRequired,
      loginRequiredMessage: 'Only Attendees can participate in Networking Sessions',
    },
  }[type];

  const getTitle = () => {
    const { title, loginRequiredMessage } = sessionTypeOptions;
    if (showLoginRequired) return loginRequiredMessage;

    if (isHappeningNow || (isPostSession && liveStatus === 'LIVE')) {
      return '';
    }

    return isPostSession ? title : `Session starts in ${timeToNow}`;
  };

  const messages = {
    title: getTitle(),
    subtitle: '',
  };

  if (!sessionTypeOptions.showSubtitle) return messages;

  switch (true) {
    case showLoginRequired: {
      messages.subtitle = `Sign in to get access to ${sessionName}`;
      return messages;
    }

    case isRtmpSession && isOrganizer: {
      messages.subtitle = 'Connect now to prepare your RTMP Stream session.';
      return messages;
    }

    case isOrganizer || isChair: {
      messages.subtitle = isLessThan1hrToStartTime
        ? 'Ready to begin? Join to make your final preparations for the live Session.'
        : 'Get ready to host the Session by checking your devices now.';
      return messages;
    }

    case isSpeaker: {
      messages.subtitle = isLessThan1hrToStartTime
        ? 'Ready to begin? Join to make your final preparations for the live Session.'
        : 'Make sure you are ready to present to the session by setting up your devices now.';
      return messages;
    }

    default:
      return messages;
  }
}

interface SessionHeaderProps {
  // general
  session: MorressierSession | MorressierNetworkingSession;
  event: Partial<MorressierEvent>;
  type: MorressierSessionType;
  sessionTime: ReturnType<typeof useSessionTime>;
  isChair: boolean;
  onBackButtonClick?: () => void;
  onEditButtonClick?: () => void;
  // PRESENTATIONS type
  backStageLink?: string;
  isOrganizer?: boolean;
  isSpeaker?: boolean;
  hasRecordings?: boolean;
  isEarlyStream?: boolean;
  liveStatus?: LiveStatus;
  // networking type
  participants?: MorressierUser[];
  joinSessionLink?: string;
  eventId: string;
}

export const HeaderSection: React.FC<SessionHeaderProps> = props => {
  const {
    session,
    event,
    type,
    isSpeaker,
    isChair,
    isOrganizer,
    onBackButtonClick = () => null,
    onEditButtonClick = () => null,
    sessionTime,
    hasRecordings,
    backStageLink,
    participants: sessionParticipants,
    joinSessionLink,
    liveStatus,
  } = props;
  const { timeToNow } = useTimeToNow(session.start_date, true);
  const { state: authState } = useAuthContext();
  const isAuthed = !!authState?.profile?.id;
  const sessionName = session?.title || session?.name;
  const authUser = authState?.profile;
  const { isPreSession, isPostSession, isHappeningNow } = sessionTime;
  const { sponsors, start_date } = session;
  const bannerUrl = session.banner_url;
  const isRtmpSession = session.stream_type === 'RTMP_STREAM';
  const isPreSessionOrHappeningNow = isPreSession || isHappeningNow;
  const isNetworkingSession = type === 'NETWORKING';

  const sponsor = !!sponsors?.length && sponsors[0];
  const reservedSpots =
    'reserved_spots_for_speakers' in session
      ? ([
          ...(session?.reserved_spots_for_speakers ?? []),
          ...(session?.chairs ?? []).map(c => c._id),
        ].filter(id => id) as string[])
      : [];
  const { usedSpots, isFull, hasReservedSpot } = getSpotsAvailability(
    reservedSpots,
    sessionParticipants,
    authUser?.id,
  );
  const disableJoinSession = !!usedSpots.isConnected || isFull || !isHappeningNow;
  const showLoginRequired = !isAuthed && isHappeningNow;
  const showHasreservedSpotMessage =
    isNetworkingSession && isHappeningNow && hasReservedSpot && !usedSpots.isConnected;

  const isLessThan1hrToStartTime =
    isPreSession && new Date(start_date).getHours() - new Date().getHours() <= 1;

  const showJoinBackStageActions =
    (isSpeaker || isOrganizer || isChair) &&
    isPreSessionOrHappeningNow &&
    session.mode === 'Live' &&
    !isRtmpSession;

  const { processAction } = useRegAndTicketingContext();

  const headerMessages = getHeaderMessages({
    event,
    type,
    hasRecordings,
    isHappeningNow,
    isPostSession,
    timeToNow,
    isRtmpSession,
    isOrganizer,
    isChair,
    isSpeaker,
    isLessThan1hrToStartTime,
    liveStatus,
    showLoginRequired,
    sessionName,
  });

  const getSponsorProps = () => {
    if (sponsor && sponsor.link) {
      return {
        href: sponsor.link,
        target: '_blank',
        rel: 'noreferrer',
        onClick: () => {
          logEvent('STANDALONE_CLICKED_SPONSOR', {
            sponsor_id: sponsor._id,
          });
        },
      };
    }

    return undefined;
  };

  const showBackground = () => {
    if (isPreSession) return true;
    if (headerMessages.title) return true;
    if (showHasreservedSpotMessage) return true;
    if (showLoginRequired) return true;
    return false;
  };

  const locationName = () => {
    let location = '';
    if (!session.room && !session.location && event.type !== 'Onsite') {
      return 'Online Session';
    }
    if (session.room) {
      location = `${session.room} `;
    }
    if (session.location) {
      if (session.room) {
        location = `${session.room}, `;
      }
      location += `${session.location} `;
    }
    return location;
  };

  const getSessionLabel = (): string => {
    let sessionLabel = '';

    if (['Onsite', 'Physical'].includes(event.type as string)) {
      sessionLabel = 'SESSION';
    } else {
      sessionLabel = type === 'PRESENTATIONS' ? 'PRESENTATION' : type;
    }

    return sessionLabel;
  };

  const sessionLocation = useMemo(() => locationName(), [locationName]);

  return (
    <>
      <Flexbox justifyContent="space-between">
        <ContainerStyles.Backbutton
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => onBackButtonClick()}
        >
          <FaChevronLeft className="mr1" />
          All Sessions
        </ContainerStyles.Backbutton>

        {isOrganizer && (
          <ContainerStyles.Backbutton
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => onEditButtonClick()}
          >
            Edit
          </ContainerStyles.Backbutton>
        )}
      </Flexbox>
      <ContainerStyles.PageContainer style={{ marginTop: '2.25rem' }}>
        <Grid container spacing={4}>
          <Grid xs={12} lg={8} item>
            {bannerUrl && (
              <Styles.BannerContainer>
                <Banner src={bannerUrl} alt={`The banner to welcome you to ${session.name}`} />
              </Styles.BannerContainer>
            )}
            <Flexbox className={bannerUrl ? 'mt2' : ''}>
              <Text color="grey">{getSessionLabel()}</Text>
              {session.featured && (
                <div className="ml1">
                  <Styles.FeaturedLabel>Featured</Styles.FeaturedLabel>
                </div>
              )}
            </Flexbox>

            <MorressierMarkdown
              source={sessionName}
              customRenderers={{
                p: pProps => <Styles.Title {...pProps} />,
              }}
            />

            <BlockText as="div" className="mb3" style={{ whiteSpace: 'pre-wrap' }}>
              <MorressierMarkdown source={session.description ?? ''} />
            </BlockText>

            <div>
              <Flexbox className="mt1" alignItems="center">
                <Styles.CalendarFa style={{ marginRight: '10px' }} />{' '}
                <Text as="span">
                  {event.type === 'Onsite' ? (
                    <DarkTooltip
                      text={getTimeText({
                        start_date: session.start_date,
                        end_date: session.end_date,
                        mode: 'Grid',
                        showFullTime: true,
                      })}
                      placement="top"
                      width="200px"
                    >
                      {getTimeText({
                        start_date: session.start_date,
                        end_date: session.end_date,
                        mode: 'Grid',
                        showFullTime: true,
                        timezone: event.timezone,
                      })}
                    </DarkTooltip>
                  ) : (
                    `${getTimeText({
                      start_date: session.start_date,
                      end_date: session.end_date,
                      mode: 'Grid',
                      showFullTime: true,
                    })} `
                  )}
                </Text>
                {isHappeningNow && (
                  <SimpleText color="red" fontWeight="bold" className="ml1">
                    Now
                  </SimpleText>
                )}
              </Flexbox>
              {sessionLocation ? (
                <Flexbox className="mt1" alignItems="center">
                  <Styles.MapMarkerAltFa style={{ marginRight: '10px' }} />{' '}
                  <Text as="span">{sessionLocation}</Text>
                </Flexbox>
              ) : null}
              <Flexbox className="mt1" alignItems="center">
                <SessionChairsWithIcon
                  eventId={event?.id as string}
                  chairs={session.chairs}
                  chairName={session.chair_name}
                />
              </Flexbox>
              {isNetworkingSession && (
                <SessionParticipantsWithImage
                  isHappeningNow={isHappeningNow}
                  isPreSession={isPreSession}
                  isPostSession={isPostSession}
                  participants={sessionParticipants ?? []}
                  reservedSpots={reservedSpots}
                />
              )}

              {(!isNetworkingSession ||
                (isNetworkingSession && !isHappeningNow) ||
                showLoginRequired ||
                showHasreservedSpotMessage) && (
                <Styles.StartTimeBox
                  hasBackground={showBackground()}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {showHasreservedSpotMessage && (
                    <Flexbox alignItems="center">
                      <Styles.ReservedSpotIcon />
                      <Text as="span" fontWeight="bold">
                        {isChair ? 'As a chair you' : 'You'} have a saved spot in this Session.
                      </Text>
                    </Flexbox>
                  )}
                  {((!isHappeningNow && headerMessages.title) || showLoginRequired) && (
                    <Flexbox>
                      {showLoginRequired ? <FaBan /> : <FaRegClock />}
                      <Flexbox>
                        <Flexbox flexDirection="column">
                          <Text as="span" fontWeight="bold">
                            {headerMessages.title}
                          </Text>
                          {((!isPostSession && session.mode === 'Live') || showLoginRequired) && (
                            <Text color="grey">{headerMessages.subtitle}</Text>
                          )}
                        </Flexbox>
                      </Flexbox>
                    </Flexbox>
                  )}
                  <Flexbox>
                    {showJoinBackStageActions && (
                      <Button
                        color="primary"
                        style={{ marginLeft: 4 }}
                        onClick={() => window.open(backStageLink)}
                      >
                        <FaVideo /> Join Backstage
                      </Button>
                    )}
                  </Flexbox>
                </Styles.StartTimeBox>
              )}
              {isOrganizer && isRtmpSession && (
                <Flexbox>
                  <RtmpHeaderOptions
                    sessionId={session._id}
                    eventId={session.event.id}
                    userId={authUser?._id}
                    showLoginRequired={showLoginRequired}
                  />
                </Flexbox>
              )}
              {isNetworkingSession && !showLoginRequired && !isPostSession && (
                <Flexbox style={{ paddingTop: '1rem' }}>
                  <Button
                    color="primary"
                    onClick={() => {
                      logEvent('CLICKED_JOIN_NETWORKING_SESSION', {
                        event_position: 'networking_header',
                        id: session._id,
                        event_id: event.id,
                      });
                      processAction(() => {
                        window.open(joinSessionLink);
                      });
                    }}
                    disabled={disableJoinSession}
                  >
                    <div
                      style={{
                        width: '1.3rem',
                        display: 'inline-block',
                        verticalAlign: 'sub',
                        marginRight: '0.5rem',
                      }}
                    >
                      <UserClassReg />
                    </div>
                    Join Session
                  </Button>
                </Flexbox>
              )}
            </div>
          </Grid>

          <Grid xs={12} lg={4} item>
            {!!sponsor && (
              <a {...getSponsorProps()} style={{ textDecoration: 'unset' }}>
                <SimpleCard
                  style={{
                    minHeight: '166px',
                    justifyContent: 'unset',
                    cursor: sponsor.link ? 'pointer' : 'unset',
                  }}
                  enableHoverState
                >
                  <Text fontWeight="bold" style={{ marginBottom: '1rem' }}>
                    Sponsored by
                  </Text>
                  <Flexbox justifyContent="center" style={{ overflow: 'hidden' }}>
                    <img
                      style={{ maxHeight: '80px', maxWidth: '100%' }}
                      src={sponsor.logo_url}
                      alt=""
                    />
                  </Flexbox>
                </SimpleCard>
              </a>
            )}
            {isNetworkingSession && !!session.labels?.length && (
              <div style={{ marginTop: '1rem' }}>
                <ContainerStyles.SubTitle>Tags</ContainerStyles.SubTitle>
                <Flexbox flexWrap="wrap" gap="1rem">
                  {session.labels?.map(label => (
                    <Styles.SessionTag key={label.key}>
                      <FaTag style={{ marginRight: '0.5rem' }} />
                      {label.label}
                    </Styles.SessionTag>
                  ))}
                </Flexbox>
              </div>
            )}
          </Grid>
        </Grid>
      </ContainerStyles.PageContainer>
    </>
  );
};
